import type { UseQueryResult } from "@tanstack/react-query";

// Utility functions used for gigs claim
import { formatISO } from "date-fns";

import type { GetMyAccountQuery, Setlist } from "@/api/queries";

import type { CountryFlag } from "../types/gigs.types";
import type { GigClaimFormValues } from "./gigsClaimSchema";

// Constants

export const FLAGS: CountryFlag[] = [
  {
    id: "default",
    onSections: [],
  },
  {
    id: "AT",
    onSections: ["promotorInfo", "admissionEUR"],
  },
  {
    id: "AU",
    onSections: ["promotorInfo"],
  },
  {
    id: "BE",
    onSections: ["promotorInfo"],
  },
  {
    id: "CA",
    onSections: ["promotorInfo", "audienceNumber", "admissionCAD", "lineUpPosition", "evidence"],
  },
  {
    id: "CH",
    onSections: ["promotorInfo"],
  },
  {
    id: "DE",
    onSections: ["lineUpPosition", "promotorInfo", "eventName"],
  },
  {
    id: "DK",
    onSections: ["promotorInfo", "audienceNumber", "admissionDKK"],
  },
  {
    id: "ES",
    onSections: ["lineUpPosition", "promotor", "otherActs", "admissionEUR"],
  },
  {
    id: "FI",
    onSections: ["promotorInfo"],
  },
  {
    id: "FR",
    onSections: ["lineUpPositionAlt", "promotor"],
  },
  {
    id: "GB",
    onSections: [],
  },
  {
    id: "NL",
    onSections: ["promotor", "admissionEUR"],
  },
  {
    id: "NO",
    onSections: ["promotorInfo"],
  },
  {
    id: "SE",
    onSections: ["promotorInfo"],
  },
  {
    id: "US",
    onSections: ["venueCapacity", "lineUpPosition", "admissionFee"],
  },
];

export const PROMOTOR_FIELD_NAMES = [
  "promotorName",
  "promotorAddress1",
  "promotorStreet",
  "promotorCity",
  "promotorPostcode",
  "promotorCountry",
  "promotorPhone",
  "promotorEmail",
] as const;

export type PromoterFieldName = typeof PROMOTOR_FIELD_NAMES[number];

export function isValidURL(url: string): boolean {
  // Regular expression for basic URL format validation
  const urlPattern
    = /^(?:https?:\/\/)?(?:[a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?::\d+)?(?:\/\S*)?$/;

  return urlPattern.test(url);
}

// gets evidence URL filename and removes prefix to show user which file they currently have uploaded in the update screen
export function extractFilenameToShow(evidenceURL: string): string {
  const filenameWithoutPrefix = evidenceURL.replace(/^[\w-]+-/, ""); // Remove UUID and hyphen
  return filenameWithoutPrefix;
}

type BaseClaimPayload = {
  performer: string | null;
  account_code: string;
  orginator_code: string | undefined;
  country: string | null;
  venueId: string | number | null;
  venuePremisesCategoryId: number | null;
  venuePremisesTypeId: number | null;
  venueName: string | null;
  prsId: string | null;
  city: string | null;
  postcode: string | null;
  address: string | null;
  date: string | null;
  time: string | null;
  evidenceURL: string | null;
  venueCapacity: string | null;
  lineUpPosition: string | null;
  promotor: string | null;
  admissionFee: string | null;
  admissionEUR: number | null;
  admissionCAD: number | null;
  admissionDKK: number | null;
  audienceNumber: number | null;
  eventName: string | null;
  eventWebsiteURL: string | null;
  otherActs: string | null;
  setlist: string | null;
  promotorName: string | null;
  promotorAddress1: string | null;
  promotorStreet: string | null;
  promotorCity: string | null;
  promotorPostcode: string | null;
  promotorCountry: string | null;
  promotorPhone: string | null;
  promotorEmail: string | null;
  festivalStageName: string | null;
  festivalPosition: string | null;
  headlineArtist: string | null;
  supportArtist1: string | null;
  supportArtist2: string | null;
  supportArtist3: string | null;
  setlistName: string | null;
  venueIsApproved: boolean;
};

type CreateClaimPayload = BaseClaimPayload & {
  works: string | null;
  other_songs: string | null;
  account_name: string;
};
type UpdateClaimPayload = BaseClaimPayload & {
  id: string | number;
};

export function buildClaimPayload(obj: GigClaimFormValues, myAccount: GetMyAccountQuery, setlists: UseQueryResult<Setlist[], Error>, id: string | number): UpdateClaimPayload;
export function buildClaimPayload(obj: GigClaimFormValues, myAccount: GetMyAccountQuery, setlists: UseQueryResult<Setlist[], Error>, id?: undefined): CreateClaimPayload;

export function buildClaimPayload(obj: GigClaimFormValues, myAccount: GetMyAccountQuery, setlists: UseQueryResult<Setlist[], Error>, id?: string | number): CreateClaimPayload | UpdateClaimPayload {
  // helper function to convert empty strings to null for strict ts expressions
  const str = (v: string | null | undefined): string | null =>
    v != null && v.length > 0 ? v : null;

  const findSetlist = (data: Setlist[]) =>
    data.find(s => String(s.id) === String(obj.setlist ?? ""));

  // check if it is updating a claim
  if (id != null) {
    const dateObj = (obj.date != null) ? formatISO(new Date(obj.date), { representation: "date" }) : null;
    const data: UpdateClaimPayload = {
      id,
      performer: str(obj.performer) ?? null,
      account_code: myAccount.data!.artistaccountcode,
      orginator_code: myAccount.data?.originatorcode?.code,
      country: str(obj.country),
      venueId: obj.venueId ?? null,
      venuePremisesCategoryId: obj.venuePremisesCategoryId ?? null,
      venuePremisesTypeId: obj.venuePremisesTypeId ?? null,
      venueName: str(obj.venueName),
      prsId: str(obj.prsId),
      city: str(obj.city),
      postcode: str(obj.postcode),
      address: str(obj.address),
      date: dateObj,
      time: str(obj.time),
      evidenceURL: str(obj.evidenceURL),
      venueCapacity: obj.venueCapacity ?? null,
      lineUpPosition: str(obj.lineUpPosition),
      promotor: str(obj.promotor),
      admissionFee: obj.admissionFee ?? null,
      admissionEUR: obj.admissionEUR ?? null,
      admissionCAD: obj.admissionCAD ?? null,
      admissionDKK: obj.admissionDKK ?? null,
      audienceNumber: obj.audienceNumber ?? null,
      eventName: str(obj.eventName),
      eventWebsiteURL: str(obj.eventWebsiteURL),
      otherActs: str(obj.otherActs),
      setlist: obj.setlist ?? null,
      promotorName: str(obj.promotorName),
      promotorAddress1: str(obj.promotorAddress1),
      promotorStreet: str(obj.promotorStreet),
      promotorCity: str(obj.promotorCity),
      promotorPostcode: str(obj.promotorPostcode),
      promotorCountry: str(obj.promotorCountry),
      promotorPhone: str(obj.promotorPhone),
      promotorEmail: str(obj.promotorEmail),
      festivalStageName: str(obj.festivalStageName),
      festivalPosition: str(obj.festivalPosition),
      headlineArtist: str(obj.headlineArtist),
      supportArtist1: str(obj.supportArtist1),
      supportArtist2: str(obj.supportArtist2),
      supportArtist3: str(obj.supportArtist3),
      setlistName: findSetlist(setlists.data ?? [])?.name ?? null,
      venueIsApproved: obj.venueIsApproved ?? false,
    };
    return data;
  }

  // return add a claim data object
  const matchedSetlist = findSetlist(setlists?.data ?? []);
  return {
    performer: str(obj.performer),
    account_code: myAccount.data!.artistaccountcode,
    orginator_code: myAccount.data?.originatorcode?.code,
    country: str(obj.country) ?? null,
    venueId: obj.venueId ?? null,
    venuePremisesCategoryId: obj.venuePremisesCategoryId ?? null,
    venuePremisesTypeId: obj.venuePremisesTypeId ?? null,
    venueName: str(obj.venueName),
    prsId: str(obj.prsId),
    city: str(obj.city),
    postcode: str(obj.postcode),
    address: str(obj.address),
    date: obj.date ?? null,
    time: str(obj.time),
    evidenceURL: str(obj.evidenceURL),
    venueCapacity: obj.venueCapacity ?? null,
    lineUpPosition: str(obj.lineUpPosition),
    promotor: str(obj.promotor),
    admissionFee: obj.admissionFee ?? null,
    admissionEUR: obj.admissionEUR ?? null,
    admissionCAD: obj.admissionCAD ?? null,
    admissionDKK: obj.admissionDKK ?? null,
    audienceNumber: obj.audienceNumber ?? null,
    eventName: str(obj.eventName),
    eventWebsiteURL: str(obj.eventWebsiteURL),
    otherActs: str(obj.otherActs),
    setlist: obj.setlist ?? null,
    promotorName: str(obj.promotorName),
    promotorAddress1: str(obj.promotorAddress1),
    promotorStreet: str(obj.promotorStreet),
    promotorCity: str(obj.promotorCity),
    promotorPostcode: str(obj.promotorPostcode),
    promotorCountry: str(obj.promotorCountry),
    promotorPhone: str(obj.promotorPhone),
    promotorEmail: str(obj.promotorEmail),
    festivalStageName: str(obj.festivalStageName),
    festivalPosition: str(obj.festivalPosition),
    headlineArtist: str(obj.headlineArtist),
    supportArtist1: str(obj.supportArtist1),
    supportArtist2: str(obj.supportArtist2),
    supportArtist3: str(obj.supportArtist3),
    setlistName: matchedSetlist?.name ?? null,
    works: matchedSetlist?.songs ?? null,
    other_songs: matchedSetlist?.other_songs ?? null,
    venueIsApproved: obj.venueIsApproved ?? false,
    account_name: myAccount.data!.accountname,
  };
}
